import React, { useEffect, useState } from 'react';
import { US_STATES } from '../../lib/usStates';
import { Network, Check, X, Plus, ShieldCheck, Clock, ArrowLeft } from 'lucide-react';
import { apiClient } from '../../lib/api';
import { useUser } from '../../store/useAuthStore';
import { ESignaturePadStep } from './ESignaturePadStep';
import { TELEMED_AGREEMENT_VERSION } from '../../content/telemedAgreement';

interface License { state: string; licenseNumber?: string; source?: 'nppes' | 'self' }
interface NetworkOption { _id: string; name: string; perVisitFeeCents: number }

type Phase = 'status' | 'form' | 'sign' | 'done';

const blobToDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

export const PrescriberNetworkSettings: React.FC = () => {
  const user = useUser();
  const pd: any = user?.physicianData || {};

  const [phase, setPhase] = useState<Phase>('status');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [licenses, setLicenses] = useState<License[]>(
    (pd.licenses || []).map((l: any) => ({ state: l.state, licenseNumber: l.licenseNumber, source: l.source }))
  );
  const [networks, setNetworks] = useState<NetworkOption[]>([]);
  const [selectedNetworkId, setSelectedNetworkId] = useState('');
  const [signatureBlob, setSignatureBlob] = useState<Blob | null>(null);

  useEffect(() => {
    if (phase === 'form' && networks.length === 0) {
      apiClient.getAvailableTelemedNetworks().then((res) => {
        if (res.success && Array.isArray(res.data)) {
          setNetworks(res.data);
          if (res.data.length === 1) setSelectedNetworkId(res.data[0]._id);
        }
      });
    }
  }, [phase, networks.length]);

  const addLicense = () => setLicenses((ls) => [...ls, { state: '', licenseNumber: '', source: 'self' }]);
  const removeLicense = (i: number) => setLicenses((ls) => ls.filter((_, idx) => idx !== i));
  const updateLicense = (i: number, field: keyof License, value: string) =>
    setLicenses((ls) => ls.map((l, idx) => (idx === i ? { ...l, [field]: value } : l)));

  const startFlow = () => { setError(''); setPhase('form'); };

  const handleFormNext = () => {
    setError('');
    const cleaned = licenses.filter((l) => l.state.trim());
    const states = cleaned.map((l) => l.state.toUpperCase());
    if (new Set(states).size !== states.length) return setError('You have duplicate states in your license list.');
    if (networks.length > 1 && !selectedNetworkId) return setError('Please select a network.');
    if (networks.length === 0) return setError('No networks are available right now — please try again later.');
    setLicenses(cleaned);
    setPhase('sign');
  };

  const handleSubmit = async () => {
    if (!signatureBlob) { setError('Please draw and capture your signature.'); return; }
    setSubmitting(true);
    setError('');
    try {
      const signatureData = await blobToDataUrl(signatureBlob);
      const res = await apiClient.requestTelemedNetwork(user!._id, {
        telemedNetworkId: selectedNetworkId || networks[0]?._id,
        licenses: licenses.filter((l) => l.state.trim()).map((l) => ({
          state: l.state.toUpperCase(),
          licenseNumber: l.licenseNumber,
          source: l.source || 'self'
        })),
        signatureData,
        agreementVersion: TELEMED_AGREEMENT_VERSION,
        agreementAccepted: true
      });
      if (!res.success) { setError(res.error || 'Failed to submit request.'); return; }
      setPhase('done');
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to submit request.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Status cards ────────────────────────────────────────────────────
  const licensedCount = (pd.licenses || []).length;

  if (phase === 'status') {
    // Telemed-only clinic — network prescribes everything.
    if (pd.telemedOnly) {
      return (
        <StatusCard icon={<ShieldCheck className="w-6 h-6 text-green-600" />} tone="green"
          title="Prescribing through the network"
          body="Your clinic has no NPI of its own, so the prescriber network writes every order based on the patient's state. There's nothing to change here." />
      );
    }
    // Hybrid coverage active.
    if (pd.useNetworkForUncoveredStates) {
      return (
        <StatusCard icon={<ShieldCheck className="w-6 h-6 text-green-600" />} tone="green"
          title="50-state coverage active"
          body={`You're licensed in ${licensedCount} state${licensedCount === 1 ? '' : 's'} and prescribe those yourself. Orders shipping to any other state are prescribed by the network (a per-visit fee applies).`} />
      );
    }
    // Pending admin approval.
    if (pd.networkGapRequested) {
      return (
        <StatusCard icon={<Clock className="w-6 h-6 text-amber-600" />} tone="amber"
          title="Request pending approval"
          body="You've signed the agreement and requested network coverage for the states you aren't licensed in. An admin will review and enable it shortly." />
      );
    }
    // Eligible NPI clinic — offer the bolt-on.
    if (pd.npiNumber) {
      return (
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <Network className="w-6 h-6 text-blue-600 shrink-0 mt-0.5" />
            <div>
              <h3 className="text-base font-semibold text-gray-900">Add the prescriber network</h3>
              <p className="text-sm text-gray-600 mt-1 max-w-xl">
                You currently prescribe under your own NPI in the states you're licensed in. Add our prescriber network to
                cover the remaining states so you can serve patients in all 50 states. You'll confirm your licenses and
                sign the Clinic Services Agreement &amp; BAA; an admin then enables it.
              </p>
            </div>
          </div>
          <button onClick={startFlow}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 font-medium">
            Get started
          </button>
        </div>
      );
    }
    // No NPI and not telemed-only — shouldn't normally happen.
    return (
      <StatusCard icon={<Network className="w-6 h-6 text-gray-500" />} tone="gray"
        title="Network coverage"
        body="Network coverage isn't available for your account configuration. Contact your account manager for help." />
    );
  }

  // ── Step 1: licenses + network ──────────────────────────────────────
  if (phase === 'form') {
    return (
      <div className="max-w-xl space-y-5">
        <button onClick={() => setPhase('status')} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div>
          <h3 className="text-base font-semibold text-gray-900">Confirm the states you're licensed in</h3>
          <p className="text-sm text-gray-600 mt-1">The network will cover the states you don't list here.</p>
        </div>
        {error && <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">{error}</div>}

        <div className="space-y-2">
          {licenses.length === 0 && <p className="text-sm text-gray-500">No licenses yet — add one below.</p>}
          {licenses.map((lic, i) => (
            <div key={i} className="flex items-center gap-2">
              <select className="px-2 py-2 border border-gray-300 rounded-lg w-24 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={lic.state} onChange={(e) => updateLicense(i, 'state', e.target.value)}>
                <option value="">State</option>
                {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <input className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="License # (optional)" value={lic.licenseNumber || ''} onChange={(e) => updateLicense(i, 'licenseNumber', e.target.value)} />
              {lic.source === 'nppes' && <span title="From registry"><Check className="w-4 h-4 text-green-500 shrink-0" /></span>}
              <button type="button" onClick={() => removeLicense(i)} className="text-gray-400 hover:text-red-500" aria-label="Remove">
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
          <button type="button" onClick={addLicense} className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700">
            <Plus className="w-4 h-4" /> Add a state
          </button>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Prescriber network</label>
          {networks.length === 0 && <p className="text-sm text-gray-500">Loading networks…</p>}
          {networks.length === 1 && (
            <div className="text-sm text-gray-700 p-3 bg-gray-50 rounded-lg">
              {networks[0].name} (${(networks[0].perVisitFeeCents / 100).toFixed(2)}/visit)
            </div>
          )}
          {networks.length > 1 && (
            <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={selectedNetworkId} onChange={(e) => setSelectedNetworkId(e.target.value)}>
              <option value="">Select a network…</option>
              {networks.map((n) => <option key={n._id} value={n._id}>{n.name} (${(n.perVisitFeeCents / 100).toFixed(2)}/visit)</option>)}
            </select>
          )}
        </div>

        <button onClick={handleFormNext} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 font-medium">
          Continue to agreement
        </button>
      </div>
    );
  }

  // ── Step 2: agreement + signature ───────────────────────────────────
  if (phase === 'sign') {
    return (
      <div>
        {error && <div className="max-w-2xl mx-auto mb-3 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">{error}</div>}
        <ESignaturePadStep
          usesNetwork
          isLoading={submitting}
          capturedSignature={signatureBlob}
          onSignatureCapture={setSignatureBlob}
          onBack={() => setPhase('form')}
          onNext={handleSubmit}
        />
      </div>
    );
  }

  // ── Done ────────────────────────────────────────────────────────────
  return (
    <StatusCard icon={<Clock className="w-6 h-6 text-amber-600" />} tone="amber"
      title="Request submitted"
      body="Thanks — your signed agreement and network request were submitted. An admin will review and enable your 50-state coverage shortly. This page will show it as active once approved." />
  );
};

const StatusCard: React.FC<{ icon: React.ReactNode; tone: 'green' | 'amber' | 'gray'; title: string; body: string }> = ({ icon, tone, title, body }) => {
  const bg = tone === 'green' ? 'bg-green-50 border-green-200' : tone === 'amber' ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-200';
  return (
    <div className={`flex items-start gap-3 p-4 border rounded-lg ${bg} max-w-xl`}>
      <div className="shrink-0 mt-0.5">{icon}</div>
      <div>
        <h3 className="text-base font-semibold text-gray-900">{title}</h3>
        <p className="text-sm text-gray-600 mt-1">{body}</p>
      </div>
    </div>
  );
};
